import { CalendarMonthOutlined } from '@mui/icons-material';
import { Box, Typography, type SxProps, type Theme } from '@mui/material';
import { designTokens } from '../tokens';
import { createInputSurfaceStyles } from '../inputSurface';

export type CompactDateFieldProps = {
  label: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  disabled?: boolean;
  sx?: SxProps<Theme>;
};

function formatDate(value: string) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  return match ? `${match[1]}년 ${match[2]}월 ${match[3]}일` : '날짜 선택';
}

/** 목록·조회 화면의 header action에 사용하는 한 줄짜리 날짜 입력입니다. */
export function CompactDateField({ label, value, onChange, required = false, disabled = false, sx }: CompactDateFieldProps) {
  return <Box
    component="label"
    data-testid="compact-date-field"
    sx={[
      {
        ...createInputSurfaceStyles({ disabled }),
        height: designTokens.layout.controlMinHeight,
        [designTokens.media.touchTarget]: { height: designTokens.layout.touchTargetMinSize },
        width: 'fit-content',
        minWidth: 0,
        flex: '0 0 auto',
        px: 1,
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 1,
        bgcolor: designTokens.color.canvas,
        opacity: disabled ? designTokens.button.disabledOpacity : 1,
      },
      ...(Array.isArray(sx) ? sx : [sx]),
    ]}
  >
    <CalendarMonthOutlined
      data-testid="compact-date-field-icon"
      aria-hidden="true"
      sx={{
        flexShrink: 0,
        fontSize: designTokens.layout.pageHeaderActionGlyphSize,
        color: designTokens.color.secondaryText,
      }}
    />
    <Typography
      data-testid="compact-date-field-label"
      component="span"
      variant="body2"
      sx={{ color: designTokens.color.secondaryText, flexShrink: 0, fontWeight: 500, whiteSpace: 'nowrap' }}
    >
      {label}
    </Typography>
    <Typography
      data-testid="compact-date-field-value"
      component="span"
      variant="body2"
      sx={{
        color: designTokens.color.primaryText,
        ...designTokens.typography.ui,
        fontWeight: 400,
        fontVariantNumeric: 'tabular-nums',
        whiteSpace: 'nowrap',
      }}
    >
      {formatDate(value)}
    </Typography>
    <Box
      component="input"
      type="date"
      aria-label={label}
      value={value}
      required={required}
      disabled={disabled}
      onChange={(event) => onChange(event.currentTarget.value)}
      onClick={(event) => {
        const input = event.currentTarget as HTMLInputElement & { showPicker?: () => void };
        input.showPicker?.();
      }}
      sx={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        p: 0,
        border: 0,
        outline: 0,
        opacity: 0,
        font: 'inherit',
        [designTokens.media.touchTarget]: { fontSize: 16 },
        cursor: disabled ? 'default' : 'pointer',
      }}
    />
  </Box>;
}
